import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function useAppLock(defaultTimeoutMinutes = 5) {
  const [isLocked, setIsLocked] = useState(false);
  const { vaultKey, lockVault } = useAuth();
  const timerRef = useRef(null);
  
  // Track last activity locally to avoid excessive re-renders
  const lastActivityRef = useRef(Date.now());

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const getEffectiveTimeout = useCallback(() => {
    try {
      // By default, Auto-Lock is DEACTIVATED (OFF)
      const isEnabled = window.localStorage.getItem('cipher-vault-autolock-enabled') === 'true';
      if (!isEnabled) {
        return 0; // 0 means Never / Deactivated
      }
      const stored = window.localStorage.getItem('cipher-vault-autolock');
      if (stored !== null && stored !== undefined) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
      return defaultTimeoutMinutes;
    } catch {
      return 0;
    }
  }, [defaultTimeoutMinutes]);

  const checkIdle = useCallback(() => {
    if (isLocked || !vaultKey) return;
    
    const timeoutMinutes = getEffectiveTimeout();
    if (timeoutMinutes <= 0) return; // 0 means Never auto-lock

    const now = Date.now();
    const idleTimeMs = now - lastActivityRef.current;
    
    if (idleTimeMs >= timeoutMinutes * 60 * 1000) {
      setIsLocked(true);
      if (lockVault) lockVault(); // Clears vaultKey from memory in AuthContext
    }
  }, [isLocked, vaultKey, getEffectiveTimeout, lockVault]);

  useEffect(() => {
    const timeoutMinutes = getEffectiveTimeout();
    // If timeout is 0, we don't auto-lock
    if (timeoutMinutes === 0 || !vaultKey) return;

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('click', handleActivity);

    timerRef.current = setInterval(checkIdle, 5000); // Check every 5s

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('click', handleActivity);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [handleActivity, checkIdle, getEffectiveTimeout, vaultKey]);

  return { isLocked, setIsLocked };
}
